/**
 * An "action screen" of a ProgressShell, which renders the interactive contents
 * of the screen and also handles navigation between screens.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";

import styleConstants from "#auth/style-constants.ts";

type Props = {
    backLabel?: string;
    backHref?: string;
    children: React.ReactNode;
    onBackClick?: (e: React.SyntheticEvent) => unknown;
    onProceedClick?: (e: React.SyntheticEvent) => unknown;
    proceedLabel?: React.ReactNode;
    proceedDisabled?: boolean;
    showBackButton?: boolean;
    showProceedButton?: boolean;
    maxWidth?: number;
};

// Submit button and navigation element for progress shell form screens.
const SUBMIT_BUTTON_HEIGHT = 40;
const SUBMIT_BUTTON_WIDTH = 160;
const SUBMIT_BUTTON_WIDTH_XS = 172;
// Height of the submit button and 8px of padding on top and bottom.
const EDGE_SPACING = 8;
const PROGRESS_NAV_HEIGHT = SUBMIT_BUTTON_HEIGHT + 2 * EDGE_SPACING;
const PROGRESS_NAV_BACK_MARGIN = 2 * EDGE_SPACING;
const PROGRESS_NAV_PROCEED_MARGIN = EDGE_SPACING;

class ProgressShellActionScreen extends React.Component<Props> {
    componentDidMount() {
        if (this._focusTarget) {
            this._focusTarget.focus();
        }
    }

    _focusTarget: HTMLElement | null | undefined = null;

    render(): React.ReactNode {
        const {
            backLabel,
            backHref,
            children,
            onBackClick,
            onProceedClick,
            proceedDisabled,
            proceedLabel,
            showBackButton,
            showProceedButton,
            maxWidth,
        } = this.props;

        return (
            <div
                className={css(styles.actionScreen)}
                style={maxWidth ? {maxWidth} : undefined}
            >
                <span tabIndex={-1} ref={(ref) => (this._focusTarget = ref)} />
                <div className={css(styles.actionScreenInner)}>{children}</div>
                {(showBackButton || showProceedButton) && (
                    <nav
                        className={css(styles.progressNav)}
                        style={
                            maxWidth
                                ? {
                                      maxWidth: `calc(${maxWidth}px - 48px)`,
                                      margin: "0 auto",
                                  }
                                : undefined
                        }
                    >
                        {showBackButton && (
                            <Button
                                onClick={onBackClick}
                                href={backHref}
                                style={styles.backLink}
                                kind="tertiary"
                            >
                                {backLabel || t`Back`}
                            </Button>
                        )}
                        {showProceedButton && proceedLabel && (
                            // @ts-expect-error - TS2745
                            <Button
                                testId="progress-shell-proceed-button"
                                style={styles.proceedButton}
                                onClick={(e) => {
                                    if (onProceedClick) {
                                        onProceedClick(e);
                                    }
                                }}
                                disabled={proceedDisabled}
                            >
                                {proceedLabel}
                            </Button>
                        )}
                    </nav>
                )}
            </div>
        );
    }
}

const styles = StyleSheet.create({
    actionScreen: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "inherit",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
    },
    actionScreenInner: {
        display: "flex",
        flexDirection: "column",
        marginLeft: "auto",
        marginRight: "auto",
        flexGrow: 1,
        marginTop: styleConstants.MOBILE_MARGIN_TOP,
        width: styleConstants.FORM_WIDTH,
        fontSize: styleConstants.BODY_FONT_SIZE,
        fontWeight: 600,
        lineHeight: 1.53,
        color: color.offBlack,
        maxWidth: `calc(100% - 2 * ${styleConstants.XS_MIN_H_PADDING}px)`,

        [mediaQueries.lgOrLarger]: {
            marginTop: styleConstants.LG_MARGIN_TOP,
        },
    },
    progressNav: {
        display: "flex",
        alignItems: "center",
        marginTop: "auto",
        backgroundColor: color.white,
        height: PROGRESS_NAV_HEIGHT,
        bottom: 0,
        left: 0,
    },
    backLink: {
        marginLeft: PROGRESS_NAV_BACK_MARGIN,
        marginRight: "auto",
        color: color.blue,
        fontSize: styleConstants.BODY_FONT_SIZE,
        lineHeight: `${SUBMIT_BUTTON_HEIGHT}px`,
        fontWeight: 600,
    },
    proceedButton: {
        marginLeft: "auto",
        marginRight: PROGRESS_NAV_PROCEED_MARGIN,
        height: SUBMIT_BUTTON_HEIGHT,
        width: SUBMIT_BUTTON_WIDTH,
        [mediaQueries.xs]: {
            width: SUBMIT_BUTTON_WIDTH_XS,
        },
    },
});

export default ProgressShellActionScreen;
